import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import AppFooter from '../components/AppFooter';

const LOGO_URL = "/logo192.png";
const BG_IMAGE = "/login-bg.svg";
const REMEMBER_KEY = 'cfp_remember_email';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Support ?next=/some/path so /join/:token can redirect back here for login
  // and bounce the user back to the invite acceptance flow afterwards.
  const nextPath = searchParams.get('next');

  // Only restore the email if the user previously checked "Remember me".
  // Password is NEVER stored or auto-filled.
  useEffect(() => {
    const savedEmail = localStorage.getItem(REMEMBER_KEY);
    if (savedEmail) {
      setEmail(savedEmail);
      setRemember(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      // Persist email only if the user explicitly chose Remember me
      if (remember) {
        localStorage.setItem(REMEMBER_KEY, email);
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
      toast.success('Welcome back!');
      navigate(nextPath && nextPath.startsWith('/') ? nextPath : '/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex">
      {/* Left side - Brand panel */}
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: `url(${BG_IMAGE})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-primary/30 to-slate-900/40" />
        <div className="relative z-10 flex flex-col justify-between w-full p-12 text-white">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="EYB" className="w-12 h-12" />
            <span className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              EYB Consulting
            </span>
          </div>

          <div className="space-y-6 max-w-lg">
            <h1
              className="text-4xl xl:text-5xl font-bold leading-tight"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Clarity and Control,
              <br />
              <span className="text-white/90">built for your business.</span>
            </h1>
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm">↔</span>
                </div>
                <div>
                  <p className="text-sm font-semibold">Live Xero sync</p>
                  <p className="text-xs text-white/70">Customers, suppliers, AR & AP</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm">⌃</span>
                </div>
                <div>
                  <p className="text-sm font-semibold">13-month forecast</p>
                  <p className="text-xs text-white/70">Down to the day</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm">$</span>
                </div>
                <div>
                  <p className="text-sm font-semibold">Multi-bank balance</p>
                  <p className="text-xs text-white/70">Up to 10 accounts</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm">✓</span>
                </div>
                <div>
                  <p className="text-sm font-semibold">Scenario testing</p>
                  <p className="text-xs text-white/70">What-if at a click</p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-xs text-white/60">
            © {new Date().getFullYear()} EYB Consulting. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50 dark:bg-slate-950">
        <Card className="w-full max-w-md shadow-lg border-slate-200 dark:border-slate-800">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden flex flex-col items-center mb-4">
              <img src={LOGO_URL} alt="Logo" className="w-16 h-16" />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-1">
              Cash Flow Timing Planner
            </p>
            <CardTitle className="text-2xl font-bold text-slate-900 dark:text-slate-50" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome
            </CardTitle>
            <CardDescription className="text-slate-500 dark:text-slate-400">
              Sign in to your EYB account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" autoComplete={remember ? 'on' : 'off'}>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11"
                  autoComplete={remember ? 'email' : 'off'}
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11"
                  autoComplete={remember ? 'current-password' : 'new-password'}
                  data-testid="login-password-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="remember-me"
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300"
                  data-testid="login-remember-checkbox"
                />
                <Label htmlFor="remember-me" className="text-sm text-slate-600 cursor-pointer">
                  Remember my email and password
                </Label>
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-primary hover:bg-primary/90 text-white font-medium"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
      </div>
      <AppFooter />
    </div>
  );
}
